import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { downloadReport } from "@/lib/report";
import { AIText } from "@/components/AIText";
import { useI18n } from "@/i18n";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft, CheckCircle2, XCircle, AlertTriangle, Sparkles, FileText, Send, Loader2, Mic, Languages, Download,
} from "lucide-react";

const money = (n) => (n || n === 0 ? `$${Number(n).toLocaleString()}` : "—");

export default function PolicyAnalysis() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [doc, setDoc] = useState(null);
  const [messages, setMessages] = useState([]);
  const [q, setQ] = useState("");
  const [asking, setAsking] = useState(false);
  const chatRef = useRef();

  useEffect(() => {
    api.get(`/documents/${id}`).then((r) => setDoc(r.data)).catch(() => { toast.error("Document not found"); navigate("/documents"); });
  }, [id, navigate]);

  useEffect(() => { chatRef.current?.scrollTo(0, chatRef.current.scrollHeight); }, [messages]);

  const ask = async (text) => {
    const question = text || q;
    if (!question.trim()) return;
    setMessages((m) => [...m, { role: "user", content: question }]);
    setQ("");
    setAsking(true);
    try {
      const res = await api.post(`/documents/${id}/ask`, { message: question, language: lang });
      setMessages((m) => [...m, { role: "assistant", content: res.data.answer }]);
    } catch { toast.error("Could not get answer"); }
    finally { setAsking(false); }
  };

  const a = doc?.analysis;
  const exportPdf = () => {
    if (!a) return;
    const line = (arr, key) => (arr || []).map((x) => `- ${x.name || x.note}${x.source ? ` (Source: ${x.source})` : ""}`).join("\n") || "None identified.";
    downloadReport({
      title: "Policy Summary", language: lang, subtitle: doc.original_filename,
      meta: {
        "Insurance type": a.insurance_type || "Not found", "Carrier": a.carrier || "Not found",
        "Annual premium": a.annual_premium ? `$${Number(a.annual_premium).toLocaleString()}` : "Not found",
        "Deductible": a.primary_deductible ? `$${Number(a.primary_deductible).toLocaleString()}` : "Not found",
        "Policy period": a.effective_date && a.expiration_date ? `${a.effective_date} – ${a.expiration_date}` : (a.effective_date || "Not found"),
      },
      sections: [
        { heading: "Summary", body: a.summary || "—" },
        { heading: "Covered", body: line((a.coverages || []).filter((c) => c.covered !== false)) },
        { heading: "Not Covered / Exclusions", body: line(a.exclusions) },
        { heading: "Important", body: line(a.important_notes) },
      ],
    });
  };
  const quick = [
    { labelKey: "q_summarize", q: "Summarize this document in plain English." },
    { labelKey: "q_exclusions", q: "What are the key exclusions in this policy?" },
    { labelKey: "q_deductible", q: "What deductible applies and to what?" },
    { labelKey: "q_catch", q: "Point out any unusual deductibles, sublimits, waiting periods or conditions I should know about. Use neutral language." },
  ];

  return (
    <AppLayout>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button onClick={() => navigate("/documents")} className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-navy">
          <ArrowLeft className="h-4 w-4" /> {t("back_to_documents")}
        </button>
        <div className="flex flex-wrap gap-2">
          {quick.map((qq) => (
            <button key={qq.q} data-testid={`quick-${qq.labelKey}`} onClick={() => ask(qq.q)}
              className="rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-navy hover:bg-slate-50">{t(qq.labelKey)}</button>
          ))}
          <button onClick={() => navigate("/voice", { state: { documentId: id } })} data-testid="talk-policy-btn"
            className="inline-flex items-center gap-2 rounded-full brand-gradient px-3.5 py-2 text-sm font-medium text-white"><Mic className="h-4 w-4" /> {t("talk_about_this")}</button>
          {a && <button onClick={exportPdf} data-testid="analysis-export"
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-navy hover:bg-slate-50"><Download className="h-4 w-4" /> {t("export_pdf")}</button>}
        </div>
      </div>

      <div className="mt-3 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-brandblue"><FileText className="h-5 w-5" /></span>
        <div>
          <h1 className="text-xl font-semibold text-navy flex items-center gap-2">{doc?.original_filename}
            {a && <span className="rounded-full bg-teal-50 px-2 py-0.5 text-xs text-brandteal">{t("analyzed")}</span>}</h1>
          <p className="text-xs text-slate-400">{t("uploaded")} {doc && new Date(doc.created_at).toLocaleDateString()}</p>
        </div>
      </div>

      {!a && <div className="mt-6 rounded-2xl border border-slate-100 bg-white p-8 text-center text-slate-400">{t("no_structured")}</div>}

      {a && (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left: Q&A + summary */}
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-3xl bg-white border border-slate-100 card-shadow p-6">
              <h2 className="font-display text-lg font-semibold text-navy flex items-center gap-2"><Sparkles className="h-5 w-5 text-brandteal" /> {t("summary")}</h2>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{a.summary || "—"}</p>
            </div>

            <div className="rounded-3xl bg-white border border-slate-100 card-shadow p-6 flex flex-col" style={{ minHeight: 380 }}>
              <h2 className="font-display text-lg font-semibold text-navy">{t("ask_about_document")}</h2>
              <div ref={chatRef} className="mt-4 flex-1 space-y-3 overflow-y-auto max-h-[320px] pr-1">
                {messages.length === 0 && <p className="text-sm text-slate-400">{t("ask_doc_hint")}</p>}
                {messages.map((m, i) => (
                  <div key={i} className={`max-w-[92%] rounded-2xl px-4 py-2.5 text-sm ${m.role === "user" ? "ml-auto bg-blue-50 text-navy" : "bg-slate-50 text-slate-700"}`}>
                    {m.role === "assistant" ? <AIText>{m.content}</AIText> : <p className="whitespace-pre-wrap">{m.content}</p>}
                  </div>
                ))}
                {asking && <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> {t("thinking")}</div>}
              </div>
              <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="mt-3 flex gap-2">
                <Input data-testid="doc-ask-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("ask_about_policy_ph")} className="rounded-full" />
                <Button type="submit" disabled={asking} className="rounded-full brand-gradient text-white px-4"><Send className="h-4 w-4" /></Button>
              </form>
            </div>
          </div>

          {/* Right: Policy at a Glance */}
          <div className="lg:col-span-3">
            <div className="rounded-3xl bg-white border border-slate-100 card-shadow p-6">
              <h2 className="font-display text-xl font-semibold text-navy flex items-center gap-2"><Sparkles className="h-5 w-5 text-brandteal" /> {t("policy_glance")}</h2>

              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-4">
                <Field label={t("fld_insurance_type")} value={a.insurance_type} nf={t("not_found_document")} />
                <Field label={t("fld_carrier")} value={a.carrier} nf={t("not_found_document")} />
                <Field label={t("fld_annual_premium")} value={a.annual_premium ? money(a.annual_premium) : null} nf={t("not_found_document")} />
                <Field label={t("fld_monthly_equiv")} value={a.monthly_equivalent ? money(a.monthly_equivalent) : (a.annual_premium ? money(Math.round(a.annual_premium / 12)) : null)} nf={t("not_found_document")} />
                <Field label={t("deductible")} value={a.primary_deductible ? money(a.primary_deductible) : null} nf={t("not_found_document")} />
                <Field label={t("fld_policy_period")} value={a.effective_date && a.expiration_date ? `${a.effective_date} – ${a.expiration_date}` : (a.effective_date || null)} nf={t("not_found_document")} />
              </div>

              {/* Covered */}
              <div className="mt-6 rounded-2xl bg-emerald-50/50 border border-emerald-100 p-4">
                <h3 className="font-semibold text-emerald-700 flex items-center gap-2"><CheckCircle2 className="h-5 w-5" /> {t("covered")}</h3>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {(a.coverages || []).filter((c) => c.covered !== false).map((c, i) => (
                    <SourceItem key={i} icon={CheckCircle2} color="text-emerald-500" name={c.name} source={c.source} srcLabel={t("source")} />
                  ))}
                  {(!a.coverages || a.coverages.length === 0) && <p className="text-sm text-slate-400">{t("no_coverages")}</p>}
                </div>
              </div>

              {/* Exclusions */}
              <div className="mt-4 rounded-2xl bg-red-50/40 border border-red-100 p-4">
                <h3 className="font-semibold text-red-600 flex items-center gap-2"><XCircle className="h-5 w-5" /> {t("not_covered")}</h3>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {(a.exclusions || []).map((c, i) => (
                    <SourceItem key={i} icon={XCircle} color="text-red-500" name={c.name} source={c.source} srcLabel={t("source")} />
                  ))}
                  {(!a.exclusions || a.exclusions.length === 0) && <p className="text-sm text-slate-400">{t("no_exclusions")}</p>}
                </div>
              </div>

              {/* Important */}
              {(a.important_notes || []).length > 0 && (
                <div className="mt-4 rounded-2xl bg-amber-50/60 border border-amber-100 p-4">
                  <h3 className="font-semibold text-amber-700 flex items-center gap-2"><AlertTriangle className="h-5 w-5" /> {t("important")}</h3>
                  <div className="mt-3 space-y-2">
                    {a.important_notes.map((n, i) => (
                      <div key={i} className="text-sm text-slate-700"><p>{n.note}</p>{n.source && <p className="text-xs text-slate-400 mt-0.5">{t("source")}: {n.source}</p>}</div>
                    ))}
                  </div>
                </div>
              )}

              <p className="mt-5 text-xs text-slate-400 flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5" /> {t("ai_disclaimer")}
              </p>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}

const Field = ({ label, value, nf }) => (
  <div>
    <p className="text-xs text-slate-400">{label}</p>
    <p className={`font-medium ${value ? "text-navy" : "text-slate-300"}`}>{value || nf}</p>
  </div>
);
const SourceItem = ({ icon: Icon, color, name, source, srcLabel }) => (
  <div className="flex items-start gap-2">
    <Icon className={`h-4.5 w-4.5 mt-0.5 ${color}`} />
    <div><p className="text-sm font-medium text-navy">{name}</p>{source && <p className="text-xs text-slate-400">{srcLabel}: {source}</p>}</div>
  </div>
);
